import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
} from '@mui/material';
import { formatTableHeader } from './tableHeader';
import { EmptyState } from './EmptyState';
import { Button } from './Button';
import './DataTable.css';
import './pipeline-matrix.css';

/**
 * Opinionated Onyx DataTable — @tanstack/react-table + Lighthouse chrome.
 * density: comfortable | compact
 * mobile: stack | scroll
 */
export function DataTable({
  columns,
  data = [],
  density = 'comfortable',
  mobile = 'stack',
  pinFirstColumn = false,
  stickyHead = false,
  enableSelection = false,
  enableSorting = true,
  selectAllScope = 'page',
  selectAllLabel = 'Select all rows',
  pageSize = 25,
  loading = false,
  emptyTitle = 'No results',
  emptyDescription,
  emptyAction,
  emptyLayout = 'state',
  onRowClick,
  toolbar,
  getRowId,
  getRowClassName,
  rowSelection: rowSelectionControlled,
  onRowSelectionChange,
  className = '',
}) {
  const [sorting, setSorting] = useState([]);
  const [uncontrolledSelection, setUncontrolledSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const selectionControlled = rowSelectionControlled !== undefined;
  const rowSelection = selectionControlled ? rowSelectionControlled : uncontrolledSelection;

  // Keep table page size in sync with the prop. initialState alone freezes the
  // first mount value (e.g. pageSize=1 while data is still loading).
  useEffect(() => {
    setPagination((prev) => (
      prev.pageSize === pageSize
        ? prev
        : { pageIndex: 0, pageSize }
    ));
  }, [pageSize]);

  const handleRowSelectionChange = (updater) => {
    const next = typeof updater === 'function' ? updater(rowSelection) : updater;
    if (!selectionControlled) setUncontrolledSelection(next);
    onRowSelectionChange?.(next);
  };

  const tableColumns = useMemo(() => {
    const cols = [...columns];
    if (enableSelection) {
      cols.unshift({
        id: '_select',
        header: ({ table: t }) => (
          <Checkbox
            size="small"
            checked={
              selectAllScope === 'all'
                ? t.getIsAllRowsSelected()
                : t.getIsAllPageRowsSelected()
            }
            indeterminate={
              selectAllScope === 'all'
                ? t.getIsSomeRowsSelected()
                : t.getIsSomePageRowsSelected()
            }
            onChange={
              selectAllScope === 'all'
                ? t.getToggleAllRowsSelectedHandler()
                : t.getToggleAllPageRowsSelectedHandler()
            }
            inputProps={{ 'aria-label': selectAllLabel }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="small"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            inputProps={{ 'aria-label': `Select row ${row.id}` }}
          />
        ),
        size: 48,
        enableSorting: false,
      });
    }
    return cols;
  }, [columns, enableSelection, selectAllScope, selectAllLabel]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, rowSelection, globalFilter, pagination },
    onSortingChange: setSorting,
    onRowSelectionChange: handleRowSelectionChange,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: enableSelection,
    getRowId: getRowId || ((row, index) => String(row.id ?? index)),
  });

  if (loading) {
    return (
      <div className="onyx-data-table onyx-data-table--loading" data-testid="onyx-data-table">
        <EmptyState title="Loading…" />
      </div>
    );
  }

  if (!data.length && emptyLayout !== 'headers') {
    return (
      <div className="onyx-data-table" data-testid="onyx-data-table">
        {toolbar}
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  return (
    <div
      className={[
        'onyx-data-table',
        `onyx-data-table--${density}`,
        `onyx-data-table--mobile-${mobile}`,
        pinFirstColumn ? 'onyx-data-table--pin-first' : '',
        stickyHead ? 'onyx-data-table--sticky-head' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="onyx-data-table"
      data-density={density}
      data-sticky-head={stickyHead ? 'true' : 'false'}
    >
      {toolbar}
      <TableContainer className="onyx-data-table__container">
        <Table size={density === 'compact' ? 'small' : 'medium'} data-variant="lighthouse">
          <TableHead>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const label = flexRender(header.column.columnDef.header, header.getContext());
                  const formatted =
                    typeof label === 'string' ? formatTableHeader(label) : label;
                  return (
                    <TableCell
                      key={header.id}
                      className="onyx-data-table__head"
                      sortDirection={
                        header.column.getIsSorted()
                          ? header.column.getIsSorted()
                          : false
                      }
                    >
                      {canSort && enableSorting ? (
                        <TableSortLabel
                          active={Boolean(header.column.getIsSorted())}
                          direction={header.column.getIsSorted() || 'asc'}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {formatted}
                        </TableSortLabel>
                      ) : (
                        formatted
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length ? table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={row.getIsSelected()}
                onClick={onRowClick ? () => onRowClick(row.original, row) : undefined}
                className={[
                  onRowClick ? 'onyx-data-table__row--clickable' : '',
                  getRowClassName ? getRowClassName(row.original, row) : '',
                ].filter(Boolean).join(' ') || undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="onyx-data-table__cell">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(table.getVisibleLeafColumns().length, 1)}
                  className="onyx-data-table__empty"
                >
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                    action={emptyAction}
                    density="compact"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {table.getPageCount() > 1 ? (
        <div className="onyx-data-table__pager">
          <Button
            variant="ghost"
            size="dense"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <span className="onyx-data-table__page">
            {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
          </span>
          <Button
            variant="ghost"
            size="dense"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  density: PropTypes.oneOf(['comfortable', 'compact']),
  mobile: PropTypes.oneOf(['stack', 'scroll']),
  pinFirstColumn: PropTypes.bool,
  stickyHead: PropTypes.bool,
  enableSelection: PropTypes.bool,
  enableSorting: PropTypes.bool,
  selectAllScope: PropTypes.oneOf(['page', 'all']),
  selectAllLabel: PropTypes.string,
  rowSelection: PropTypes.object,
  onRowSelectionChange: PropTypes.func,
  pageSize: PropTypes.number,
  loading: PropTypes.bool,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.node,
  emptyAction: PropTypes.node,
  emptyLayout: PropTypes.oneOf(['state', 'headers']),
  onRowClick: PropTypes.func,
  toolbar: PropTypes.node,
  getRowId: PropTypes.func,
  getRowClassName: PropTypes.func,
  className: PropTypes.string,
};

/** Column helpers for consistent cell chrome */
export const col = {
  text: (id, header, accessorKey) => ({
    id,
    accessorKey: accessorKey || id,
    header,
    cell: (info) => info.getValue() ?? '—',
  }),
  number: (id, header, accessorKey) => ({
    id,
    accessorKey: accessorKey || id,
    header,
    cell: (info) => {
      const v = info.getValue();
      return v == null ? '—' : Number(v).toLocaleString();
    },
  }),
  code: (id, header, accessorKey) => ({
    id,
    accessorKey: accessorKey || id,
    header,
    cell: (info) => {
      const v = info.getValue();
      return v == null || v === '' ? '—' : (
        <code className="onyx-data-table__code">{v}</code>
      );
    },
  }),
};

/**
 * DataMatrix — wide entity×criteria grid. Always scrolls on mobile; pins first column.
 */
export function DataMatrix(props) {
  return (
    <DataTable
      {...props}
      density={props.density || 'compact'}
      mobile="scroll"
      pinFirstColumn
    />
  );
}

DataMatrix.propTypes = DataTable.propTypes;

export default DataTable;
